import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// BPM / Musical Key / Camelot helpers shared by filters and cards.
export const BPM_PRESETS = [
  { label: "70–90", min: 70, max: 90 },
  { label: "90–100", min: 90, max: 100 },
  { label: "100–110", min: 100, max: 110 },
  { label: "110–120", min: 110, max: 120 },
  { label: "120–128", min: 120, max: 128 },
  { label: "128–140", min: 128, max: 140 },
  { label: "140+", min: 140, max: null },
];

const MINOR = ["G#", "D#", "A#", "F", "C", "G", "D", "A", "E", "B", "F#", "C#"];
const MAJOR = ["B", "F#", "C#", "G#", "D#", "A#", "F", "C", "G", "D", "A", "E"];
const NOTE_ORDER = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const CODE_RE = /^(1[0-2]|[1-9])[AB]$/;

export const CAMELOT_WHEEL = Array.from({ length: 12 }, (_, i) => [
  { code: `${i + 1}A`, key: `${MINOR[i]} minor` },
  { code: `${i + 1}B`, key: `${MAJOR[i]} major` },
]).flat();

export const EMPTY_ATTRS = { bpm_min: "", bpm_max: "", keys: [], camelot: [], compatible: false };

// Musical keys listed by name; each maps to its Camelot code.
export const MUSICAL_KEYS = [
  ...MAJOR.map((k, i) => ({ code: `${i + 1}B`, label: k, mode: "major" })),
  ...MINOR.map((k, i) => ({ code: `${i + 1}A`, label: `${k}m`, mode: "minor" })),
].sort((a, b) => a.mode.localeCompare(b.mode) || NOTE_ORDER.indexOf(a.label.replace("m", "")) - NOTE_ORDER.indexOf(b.label.replace("m", "")));

export function attrsFromParams(sp) {
  const camelot = (sp.get("camelot") || "").split(",").filter((c) => CODE_RE.test(c));
  const keys = (sp.get("key") || "").split(",").filter((c) => CODE_RE.test(c));
  const num = (v) => (v && !Number.isNaN(Number(v)) ? v : "");
  return {
    bpm_min: num(sp.get("bpm_min")),
    bpm_max: num(sp.get("bpm_max")),
    keys,
    camelot,
    compatible: sp.get("compatible") === "1" && (camelot.length > 0 || keys.length > 0),
  };
}

export function attrsToQuery(a) {
  const p = new URLSearchParams();
  if (a.bpm_min !== "" && a.bpm_min != null) p.set("bpm_min", String(a.bpm_min));
  if (a.bpm_max !== "" && a.bpm_max != null) p.set("bpm_max", String(a.bpm_max));
  if (a.keys?.length) p.set("key", a.keys.join(","));
  if (a.camelot?.length) p.set("camelot", a.camelot.join(","));
  if (a.compatible && (a.camelot?.length || a.keys?.length)) p.set("compatible", "1");
  return p.toString();
}

// API takes one Camelot list: union of key-picker and Camelot-picker selections.
export function attrsToApiQuery(a) {
  const union = [...new Set([...(a.keys || []), ...(a.camelot || [])])];
  return attrsToQuery({ ...a, keys: [], camelot: union });
}

export function applyAttrsToParams(sp, a) {
  const next = new URLSearchParams(sp);
  ["bpm_min", "bpm_max", "key", "camelot", "compatible"].forEach((k) => next.delete(k));
  new URLSearchParams(attrsToQuery(a)).forEach((v, k) => next.set(k, v));
  return next;
}

export const hasAttrs = (a) => Boolean(a.bpm_min || a.bpm_max || a.camelot?.length || a.keys?.length);

let availPromise = null;
// Which filters have public data ({bpm, key} counts); one request per page load.
export function useAttrAvailability() {
  const [v, setV] = useState(null);
  useEffect(() => {
    availPromise = availPromise || api.get("/site/music-attrs").then((r) => r.data).catch(() => ({ bpm: 1, key: 0 }));
    let live = true;
    availPromise.then((d) => live && setV(d));
    return () => { live = false; };
  }, []);
  return v;
}

// DJ (Mixed In Key style) short key names per Camelot code: 8A = Am, 6B = Bb.
const DJ_MINOR = ["Abm", "Ebm", "Bbm", "Fm", "Cm", "Gm", "Dm", "Am", "Em", "Bm", "F#m", "Dbm"];
const DJ_MAJOR = ["B", "F#", "Db", "Ab", "Eb", "Bb", "F", "C", "G", "D", "A", "E"];
export const shortKey = (camelot) => {
  const m = CODE_RE.exec(camelot || "");
  return m ? (camelot.endsWith("A") ? DJ_MINOR : DJ_MAJOR)[Number(m[1]) - 1] : "";
};
// 24 selectable keys for admin forms; value is the Camelot code (backend accepts '8A').
export const KEY_OPTIONS = Array.from({ length: 12 }, (_, i) => [
  { value: `${i + 1}A`, label: `${DJ_MINOR[i]} · ${i + 1}A` },
  { value: `${i + 1}B`, label: `${DJ_MAJOR[i]} · ${i + 1}B` },
]).flat();
// "110 BPM · Am · 8A" (missing parts are skipped).
export const musicLine = (t) => [
  t?.bpm ? `${Math.round(t.bpm)} BPM` : "", shortKey(t?.camelot), t?.camelot || "",
].filter(Boolean).join(" · ");
